import type { TFunction } from "i18next";

import { ReactLazyLoading } from "@/containers/ReactLoading";
import { allianzBuergerNextContactValidation } from "@/customers/allianz-vertretung-claus-buerger/contacts/helpers";
import { saveTask as saveAllianzTask } from "@/customers/allianz-vertretung-claus-buerger/Tasks/Form/saveTask";
import { brinkmannNextContactValidation } from "@/customers/brinkmann/contacts/helpers";
import { saveTask as saveBrinkmannTask } from "@/customers/brinkmann/Tasks/Form/saveTask";
import { saveTask as saveCitycleanTask } from "@/customers/cityclean/kundenrueckgewinnung/Tasks/saveTask";
import { saveTask as saveCitycleanNeugruenderTask } from "@/customers/cityclean/neugruender/Tasks/Form/saveTask";
import { citycleanSaisonkundenNextContactValidation } from "@/customers/cityclean/saisonkunden/contacts/helpers";
import { saveTask as saveCitycleanSaisonTask } from "@/customers/cityclean/saisonkunden/Tasks/Form/saveTask";
import { gjuDetailsLabel, isGjuManagedContact } from "@/customers/gju-demo/contacts/helpers";
import { saveTask as saveGjuTask } from "@/customers/gju-demo/Tasks/Form/saveTask";
import { infinityMediaNextContactValidation } from "@/customers/infinity-media/contacts/helpers";
import { saveTask as saveInfinityMediaTask } from "@/customers/infinity-media/Tasks/Form/saveTask";
import { mercedesNextContactValidation } from "@/customers/mercedes-benz/contacts/selbach/helpers";
import { saveTask as saveMercedesDefaultTask } from "@/customers/mercedes-benz/Tasks/default/Form/saveTask";
import { saveTask as saveMercedesTask } from "@/customers/mercedes-benz/Tasks/van-bestandsfahrzeuge/Form/saveTask";
import { saveTask as saveProxytelTask } from "@/customers/proxytel/vertrieb/Tasks/Form/saveTask";
import { quantiquestNextContactValidation } from "@/customers/quantiquest/contacts/siegel/helpers";
import { quantiquestAnwaltNextContactValidation } from "@/customers/quantiquest/contacts/top-anwalt/helpers";
import { saveTask as saveQuantiquestDefaultTask } from "@/customers/quantiquest/Tasks/default/Form/saveTask";
import { saveTask as saveQuantiquestSiegelTask } from "@/customers/quantiquest/Tasks/siegel/Form/saveTask";
import { sugNextContactValidation } from "@/customers/sug/contacts/ProjectComponent/new/helpers";
import { saveTask as saveTermitelVertriebTask } from "@/customers/termitel/Vertrieb/Tasks/Form/saveTask";
import { wuestenrotNextContactValidation } from "@/customers/wuestenrot-sascha-maurer/contacts/helpers";
import { saveTask as saveWuestenrotTask } from "@/customers/wuestenrot-sascha-maurer/Tasks/Form/saveTask";
import type { ContactStatusFragment } from "@/gql/graphql";
import { useAppSelector } from "@/hooks";
import { sessionSelector } from "@/store/sessionReducer";

import { type QuestionnaireComponentType } from "./show/Questionnaire";
import type { AddfieldsPropsInterface } from "./show/types";

const SugContact = ReactLazyLoading(() => import("@/customers/sug/contacts/ProjectComponent"));

type StatusModalAddfieldsPropsType = {
  contact: Nullable<ContactFull>;
  states: ContactStatusFragment[];
};

export type ProjectPropsType = {
  component: QuestionnaireComponentType;
  type?: string;
  label?: (contact: ContactFull) => string;
  visible?: (contact: ContactFull) => boolean;
  defaultActive?: boolean;
};
type AddfieldsPropsType = AddfieldsPropsInterface;
type HistoriesTodoPropsType = { contact: ContactFull };
type HistoriesCustomerContactPropsType = { contact: ContactFull };
type HistoriesMailPropsType = { contact: ContactFull };

type ComponentBag = {
  list: React.ComponentType;
  new: React.ComponentType;
  show: React.ComponentType;
  "status-modal/addfields"?: React.ComponentType<StatusModalAddfieldsPropsType>;
  agenda: React.ComponentType;
  project?: ProjectPropsType;
  addfields?: React.ComponentType<AddfieldsPropsType>;
  "histories/todos": React.ComponentType<HistoriesTodoPropsType>;
  "histories/customerContacts": React.ComponentType<HistoriesCustomerContactPropsType>;
  "histories/mails": React.ComponentType<HistoriesMailPropsType>;

  "histories/callback"?: (
    session: SessionInterface,
    contact: ContactRefType,
    history: HistoryInterface,
  ) => Promise<void>;

  searchbar?: React.ComponentType;
  pipelineFilters?: React.ComponentType;
  export?: React.ComponentType;

  saveTask?: (
    session: SessionInterface,
    existingTask: Nilable<TaskRef>,
    states: ContactStatusFragment[],
    values: any,
    t: TFunction,
  ) => Promise<CCTaskType | undefined>;

  pipelineValidation: (
    contact: ContactFull,
    lastcallContact: Nilable<ContactRefType>,
    questionnaire: Nullable<CCTaskType>,
    events: EventDetailWoContact[],
  ) => [boolean, string];
};

// eslint-disable-next-line react-refresh/only-export-components
export const COMPONENTS: TTDict<string, TTDict<string, Partial<ComponentBag>>> = {
  "sug-ag": {
    "vertrieb-offensive-084": {
      project: {
        component: ReactLazyLoading(() => import("@/customers/sug/contacts/ProjectComponent/new-no-mbvs")),
      },
      searchbar: ReactLazyLoading(() => import("@/customers/sug/contacts/SearchBar")),
      pipelineValidation: sugNextContactValidation,
    },
    default: {
      project: {
        component: SugContact,
      },
      searchbar: ReactLazyLoading(() => import("@/customers/sug/contacts/SearchBar")),
      pipelineValidation: sugNextContactValidation,
    },
  },
  "sug-gmbh": {
    default: {
      project: {
        component: SugContact,
      },
      searchbar: ReactLazyLoading(() => import("@/customers/sug/contacts/SearchBar")),
      pipelineValidation: sugNextContactValidation,
    },
  },

  "sevenit-gmbh": {
    default: {
      list: ReactLazyLoading(() => import("@/customers/sevenit/contacts/List")),
      addfields: ReactLazyLoading(() => import("@/customers/sevenit/contacts/show/projects/Default")),
    },
  },

  "lr-health-beauty": {
    default: {
      addfields: ReactLazyLoading(() => import("@/customers/lr-health-beauty/service/contacts/Service")),
    },
    "outbound-exit": {
      addfields: ReactLazyLoading(() => import("@/customers/lr-health-beauty/service/contacts/Service")),
    },
    vertrieb: {
      addfields: ReactLazyLoading(() => import("@/customers/lr-health-beauty/vertrieb/Contacts/ProjectComponent")),
      searchbar: ReactLazyLoading(() => import("@/customers/lr-health-beauty/vertrieb/Contacts/Searchbar")),
    },
  },

  "lr-france": {
    frankreich: {
      addfields: ReactLazyLoading(() => import("@/customers/lr-health-beauty/service/contacts/Service")),
    },

    service: {
      addfields: ReactLazyLoading(() => import("@/customers/lr-france/contacts/show/projects/ServiceFrance")),
      list: ReactLazyLoading(() => import("@/customers/lr-france/contacts/List")),
    },
  },

  proxytel: {
    vertrieb: {
      saveTask: saveProxytelTask,
      agenda: ReactLazyLoading(() => import("@/customers/proxytel/contacts/Agenda")),
    },
  },

  termitel: {
    bewerbermanagement: {
      project: {
        component: ReactLazyLoading(() => import("@/customers/termitel/contacts/show/projects/Bewerbermanagement")),
      },
    },
    vertrieb: {
      pipelineValidation: () => [true, ""],
      pipelineFilters: ReactLazyLoading(() => import("@/customers/termitel/Vertrieb/PipelineFilters")),
      searchbar: ReactLazyLoading(() => import("@/customers/termitel/Vertrieb/PipelineFilters")),
      saveTask: saveTermitelVertriebTask,
      project: {
        component: ReactLazyLoading(() => import("@/customers/termitel/contacts/Questionnaire")),
      },
    },
    projektmanagement: {
      project: {
        component: ReactLazyLoading(() => import("@/customers/termitel/contacts/show/projects/Projektmanagement")),
      },
    },
  },

  "mercedes-benz": {
    "van-bestandsfahrzeuge": {
      saveTask: saveMercedesTask,
      project: {
        component: ReactLazyLoading(
          () => import("@/customers/mercedes-benz/contacts/van-bestandsfahrzeuge/show/Questionare"),
        ),
      },
      searchbar: ReactLazyLoading(() => import("@/customers/mercedes-benz/contacts/van-bestandsfahrzeuge/Searchbar")),
      agenda: ReactLazyLoading(() => import("@/customers/mercedes-benz/contacts/van-bestandsfahrzeuge/Agenda")),
    },
    "paritaetischer-wohlfahrtsverband": {
      project: {
        component: ReactLazyLoading(
          () => import("@/customers/mercedes-benz/contacts/paritaetischer-wohlfahrtsverband/Questionnaire"),
        ),
      },
      saveTask: saveMercedesDefaultTask,
    },
    selbach: {
      project: {
        component: ReactLazyLoading(() => import("@/customers/mercedes-benz/contacts/selbach/Questionnaire")),
      },
      saveTask: saveMercedesDefaultTask,
      pipelineValidation: mercedesNextContactValidation,
      searchbar: ReactLazyLoading(() => import("@/customers/mercedes-benz/contacts/selbach/Searchbar")),
    },
    "transporter-rheinland": {
      project: {
        component: ReactLazyLoading(
          () => import("@/customers/mercedes-benz/contacts/transporter-rheinland/Questionnaire"),
        ),
      },
      saveTask: saveMercedesDefaultTask,
      searchbar: ReactLazyLoading(() => import("@/customers/mercedes-benz/contacts/transporter-rheinland/Searchbar")),
      pipelineValidation: mercedesNextContactValidation,
    },
  },

  zimmermann: {
    default: {
      new: ReactLazyLoading(() => import("@/customers/zimmermann/contacts/New")),
    },
  },

  siwave: {
    default: {
      new: ReactLazyLoading(() => import("@/customers/siwave/contacts/New")),
      project: {
        component: ReactLazyLoading(() => import("@/customers/siwave/contacts/ProjectComponent")),
      },
    },
  },

  "allianz-vertretung-claus-buerger": {
    default: {
      saveTask: saveAllianzTask,
      project: {
        component: ReactLazyLoading(
          () => import("@/customers/allianz-vertretung-claus-buerger/contacts/ProjectComponent"),
        ),
      },
      pipelineValidation: allianzBuergerNextContactValidation,
    },
  },

  quantiquest: {
    siegel: {
      project: {
        component: ReactLazyLoading(() => import("@/customers/quantiquest/contacts/siegel/ProjectComponent")),
      },
      pipelineValidation: quantiquestNextContactValidation,
      saveTask: saveQuantiquestSiegelTask,
    },
    fahrschulen: {
      project: {
        component: ReactLazyLoading(() => import("@/customers/quantiquest/contacts/fahrschulen/ProjectComponent")),
      },
      saveTask: saveQuantiquestDefaultTask,
      searchbar: ReactLazyLoading(() => import("@/customers/quantiquest/contacts/fahrschulen/AdditionalFilters")),
    },
    "top-anwalt": {
      project: {
        component: ReactLazyLoading(() => import("@/customers/quantiquest/contacts/top-anwalt/ProjectComponent")),
      },
      saveTask: saveQuantiquestDefaultTask,
      pipelineValidation: quantiquestAnwaltNextContactValidation,
    },
    "top-makler": {
      project: {
        component: ReactLazyLoading(() => import("@/customers/quantiquest/contacts/top-makler/ProjectComponent")),
      },
      saveTask: saveQuantiquestDefaultTask,
      pipelineValidation: quantiquestAnwaltNextContactValidation,
    },
    "top-berater": {
      project: {
        component: ReactLazyLoading(() => import("@/customers/quantiquest/contacts/top-berater/ProjectComponent")),
      },
      saveTask: saveQuantiquestDefaultTask,
      pipelineValidation: quantiquestAnwaltNextContactValidation,
    },
  },

  brinkmann: {
    default: {
      project: {
        component: ReactLazyLoading(() => import("@/customers/brinkmann/contacts/Questionnaire")),
      },
      saveTask: saveBrinkmannTask,
      searchbar: ReactLazyLoading(() => import("@/customers/brinkmann/contacts/Searchbar")),
      pipelineValidation: brinkmannNextContactValidation,
      addfields: ReactLazyLoading(() => import("@/customers/brinkmann/contacts/show/Addfields")),
    },
  },

  cityclean: {
    kundenrueckgewinnung: {
      project: {
        component: ReactLazyLoading(
          () => import("@/customers/cityclean/kundenrueckgewinnung/Contacts/ProjectComponent"),
        ),
      },
      saveTask: saveCitycleanTask,
    },
    neugruender: {
      project: {
        component: ReactLazyLoading(() => import("@/customers/cityclean/neugruender/contacts/Questionnaire")),
      },
      saveTask: saveCitycleanNeugruenderTask,
    },
    saisonkunden: {
      project: {
        component: ReactLazyLoading(() => import("@/customers/cityclean/saisonkunden/contacts/Questionnaire")),
      },
      saveTask: saveCitycleanSaisonTask,
      pipelineValidation: citycleanSaisonkundenNextContactValidation,
    },
  },

  kesseboehmer: {
    default: {
      project: {
        component: ReactLazyLoading(() => import("@/customers/kesseboehmer/contacts/ProjectComponent")),
      },
    },
  },

  "wuestenrot-sascha-maurer": {
    default: {
      project: {
        component: ReactLazyLoading(() => import("@/customers/wuestenrot-sascha-maurer/contacts/Questionnaire")),
      },
      saveTask: saveWuestenrotTask,
      pipelineValidation: wuestenrotNextContactValidation,
    },
  },

  "infinity-media": {
    default: {
      project: {
        component: ReactLazyLoading(() => import("@/customers/infinity-media/contacts/Questionnaire")),
      },
      saveTask: saveInfinityMediaTask,
      pipelineValidation: infinityMediaNextContactValidation,
    },
  },

  "gju-demo": {
    demo: {
      new: ReactLazyLoading(() => import("@/customers/gju-demo/contacts/New")),
      list: ReactLazyLoading(() => import("@/customers/gju-demo/contacts/List")),
      project: {
        type: "gju-record-details",
        component: ReactLazyLoading(() => import("@/customers/gju-demo/contacts/RecordDetails")),
        label: gjuDetailsLabel,
        visible: isGjuManagedContact,
        defaultActive: true,
      },
      saveTask: saveGjuTask,
    },
  },
};

// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT: ComponentBag = {
  "histories/todos": ReactLazyLoading(() => import("./show/Histories/Todos")),
  "histories/customerContacts": ReactLazyLoading(() => import("./show/Histories/Tasks")),
  "histories/mails": ReactLazyLoading(() => import("./show/Histories/Mails")),
  list: ReactLazyLoading(() => import("./List")),
  new: ReactLazyLoading(() => import("./New")),
  show: ReactLazyLoading(() => import("./show")),
  agenda: ReactLazyLoading(() => import("./Agenda")),
  pipelineValidation: () => [false, ""],
};

type PropsType =
  | { name: "list" | "new" | "show" | "agenda" | "searchbar" | "pipelineFilters" | "export" }
  | ({ name: "status-modal/addfields" } & StatusModalAddfieldsPropsType)
  | ({ name: "histories/todos" } & HistoriesTodoPropsType)
  | ({ name: "histories/customerContacts" } & HistoriesCustomerContactPropsType)
  | ({ name: "histories/mails" } & HistoriesMailPropsType);

export default function SpecificComponent({ name, ...props }: PropsType) {
  const { currentCustomer, currentProject } = useAppSelector(sessionSelector);

  const Component: any =
    COMPONENTS[currentCustomer.identifier]?.[currentProject.identifier]?.[name] ||
    COMPONENTS[currentCustomer.identifier]?.default?.[name] ||
    DEFAULT[name];

  if (!Component) {
    return null;
  }

  return <Component {...props} />;
}
